import { HeartHandshake, ClipboardCheck, MapPin, Clock } from "lucide-react";
import { IMAGES } from "@/lib/site";
import { Reveal } from "@/components/motion-primitives";
import { PageHero, CtaBand, SectionHead, usePageMeta } from "@/components/shared";

const VALUES = [
  [HeartHandshake, "Personal Service", "You will always know exactly who is caring for your home — and reach them directly."],
  [ClipboardCheck, "Professional Standards", "Written checklists, documented inspections and accountable follow-through on every detail."],
  [MapPin, "Local Knowledge", "Born of the island: the trades, the seasons, the guests and the specifics only a Bermudian team knows."],
  [Clock, "Calm Reliability", "Problems handled before you hear about them. Updates before you think to ask."],
];

export default function About() {
  usePageMeta("About", "The Bermuda Co-Host — local knowledge, personal service and professional standards for Bermuda vacation rental owners.");
  return (
    <main>
      <PageHero
        overline="About The Bermuda Co-Host"
        lines={["Local Knowledge.", "Personal Service.", "Professional Standards."]}
        copy="The Bermuda Co-Host was created to give Bermuda homeowners something that was missing: a dependable local person who could care for their property — while delivering an experience visitors remember."
        image={IMAGES.villaPool}
      />

      <section className="mx-auto max-w-[1440px] px-6 py-24 lg:px-10 lg:py-32">
        <div className="grid gap-14 lg:grid-cols-12 lg:gap-20">
          <Reveal className="lg:col-span-5">
            <div className="relative flex aspect-[4/5] items-center justify-center overflow-hidden bg-navy" data-testid="founder-portrait-frame">
              <div className="noise-overlay absolute inset-0" />
              <div className="absolute inset-0 bg-gradient-to-t from-navy via-transparent to-navy/60" />
              <div className="relative px-10 text-center">
                <img src="/logo-stacked-white.png" alt="The Bermuda Co-Host" className="mx-auto h-40 w-auto opacity-95" />
                <p className="mt-8 font-serif text-2xl font-light italic leading-snug text-ivory/90">
                  "Hospitality is not what I do.<br />It is who I am."
                </p>
                <p className="mt-5 text-[10px] uppercase tracking-[0.3em] text-ivory/50">The Founder · Bermuda</p>
              </div>
            </div>
          </Reveal>
          <div className="lg:col-span-7">
            <SectionHead overline="Behind the Standard" title="A hospitality mastermind. A hand-picked island team." />
            <div className="mt-8 space-y-6 text-base leading-relaxed text-navy/70">
              <Reveal>
                <p>
                  The founder of The Bermuda Co-Host has worked in hospitality since the age of fourteen — inside
                  Bermuda's best hotels and restaurants, across food service, concierge, laundry, personal
                  assistance, food &amp; beverage supervision, social media management, interior design and events.
                  Few people on the island have seen exceptional service from so many angles.
                </p>
              </Reveal>
              <Reveal delay={0.08}>
                <p>
                  Through all of it, one thing never changed: a genuine love for people, and for creating
                  unforgettable experiences. The Bermuda Co-Host was born from watching homeowners struggle with the
                  same pattern — a beautiful property, a demanding rental operation, and no dependable local person
                  to bridge the two.
                </p>
              </Reveal>
              <Reveal delay={0.16}>
                <p>
                  So she built that bridge — and surrounded herself with a hand-picked team of cleaners, inspectors
                  and coordinators held to the same obsessive standard. Not a faceless platform, not a cleaning crew
                  with a website: a genuine property partner. Someone who answers the guest's midnight message,
                  walks the house after a storm, holds every clean to a hotel standard and tells you the truth about
                  your home before anyone else does.
                </p>
              </Reveal>
              <Reveal delay={0.22}>
                <div className="flex flex-wrap gap-2.5 pt-2" data-testid="founder-background-chips">
                  {["Food Service", "Concierge", "Laundry", "Personal Assistant", "F&B Supervisor", "Social Media", "Interior Design", "Events"].map((chip) => (
                    <span key={chip} className="border border-navy/15 bg-sand-light px-4 py-2 text-[11px] font-medium uppercase tracking-[0.14em] text-navy/70">
                      {chip}
                    </span>
                  ))}
                </div>
              </Reveal>
            </div>
          </div>
        </div>

        <div className="mt-24 grid gap-px bg-navy/10 sm:grid-cols-2 lg:grid-cols-4">
          {VALUES.map(([Icon, title, copy], i) => (
            <Reveal key={title} delay={i * 0.06} className="h-full">
              <div className="h-full bg-white p-8">
                <span className="flex h-10 w-10 items-center justify-center rounded-full bg-pink-soft text-bermuda-dark">
                  <Icon size={17} strokeWidth={1.5} />
                </span>
                <h3 className="mt-5 font-serif text-xl text-navy">{title}</h3>
                <p className="mt-2.5 text-[13px] leading-relaxed text-navy/60">{copy}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      <CtaBand title="Meet the team behind the standard." copy="Book a consultation — a relaxed conversation about your property and whether we are the right fit." />
    </main>
  );
}
